import { FC, useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { treatmentPlanService } from "../../services/treatmentPlan.service";
import {
	PropagationJob,
	PropagationParticipants,
	historyAcknowledgment,
	historyCounts,
	historyReachedEveryone,
	historyWhatChanged,
	participantOutcomeLabel,
} from "./propagationStatus";

/**
 * SIMS-9024 — the threshold changes applied to a plan, and who each one covered.
 *
 * A DIALOG rather than a section on the form: TreatmentPlanForm is already long and dense, and an
 * inline history would push the plan fields down for every administrator including those who
 * never open it.
 *
 * READ-ONLY. Nothing here writes, which is also how SIMS-9037 AC4 ("entries are never altered")
 * is satisfied — there is no affordance to alter one.
 *
 * PARTICIPANTS ARE DISPLAYED, NOT SEARCHED (PO, 2026-09-22 demo; SIMS-9024 comment 66232). The
 * earlier build asked the agent to type an enrollment reference, which they had no way to obtain.
 * Loaded PER ENTRY on expand, never with the list: the history list carries no participant ids at
 * all, so one response can never hold several cohorts.
 *
 * IDS ONLY, NO NAMES. Names are PHI; the ids are what a support agent takes to another system.
 * NO EXPORT, PRINT OR COPY CONTROL — a Product Owner condition (comment 66185) and a compliance
 * constraint (CR-4 / OQ-17, both OPEN). Adding either means going back to the PO. Tests assert
 * both absences, mirroring TC-LIST-005 for the SIMS-9023 forecast list.
 */

export interface ThresholdHistoryDialogProps {
	open: boolean;
	onClose: () => void;
	treatmentPlanId: string;
	planName?: string;
}

const formatWhen = (iso?: string): string => {
	if (!iso) {
		return "—";
	}
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/** CR-1: a change that can end an enrollment must be attributable to a person. */
const formatActor = (createdBy: PropagationJob["createdBy"]): string => {
	if (!createdBy) {
		return "Unknown administrator";
	}
	// An UNPOPULATED reference — the backend's populate was dropped, or this job's user no longer
	// resolves. Printing it would put a 24-character hex string where the name belongs, which
	// reads as a rendering fault rather than as missing attribution.
	if (typeof createdBy === "string") {
		return "Unknown administrator";
	}
	const name = [createdBy.firstName, createdBy.lastName]
		.filter(Boolean)
		.join(" ")
		.trim();
	return name || "Unknown administrator";
};

const ThresholdHistoryDialog: FC<ThresholdHistoryDialogProps> = ({
	open,
	onClose,
	treatmentPlanId,
	planName,
}) => {
	const [jobs, setJobs] = useState<PropagationJob[] | null>(null);
	const [error, setError] = useState(false);
	const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
	const [cohort, setCohort] = useState<PropagationParticipants | null>(null);
	const [loadingCohort, setLoadingCohort] = useState(false);
	const [cohortError, setCohortError] = useState(false);

	const load = useCallback(async () => {
		setError(false);
		try {
			setJobs(
				await treatmentPlanService.getThresholdPropagationHistory(
					treatmentPlanId,
				),
			);
		} catch {
			// An empty history and a failed read are different things and must not look alike:
			// AC5's "nothing to show" is a real answer, and showing it after a failure would
			// tell the administrator the plan has never had a change when it may well have.
			setJobs(null);
			setError(true);
		}
	}, [treatmentPlanId]);

	useEffect(() => {
		if (open) {
			void load();
		} else {
			// Leave nothing behind for the next plan opened.
			setExpandedJobId(null);
			setCohort(null);
			setCohortError(false);
		}
	}, [open, load]);

	const toggleParticipants = async (jobId: string): Promise<void> => {
		if (expandedJobId === jobId) {
			setExpandedJobId(null);
			setCohort(null);
			return;
		}

		setExpandedJobId(jobId);
		setCohort(null);
		setCohortError(false);
		setLoadingCohort(true);
		try {
			setCohort(
				await treatmentPlanService.getThresholdPropagationParticipants(
					treatmentPlanId,
					jobId,
				),
			);
		} catch {
			setCohortError(true);
		} finally {
			setLoadingCohort(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			data-testid="threshold-history-dialog"
		>
			<DialogTitle>
				Threshold change history
				{planName && (
					<Typography variant="body2" color="text.secondary">
						{planName}
					</Typography>
				)}
			</DialogTitle>

			<DialogContent dividers>
				{error && (
					<Alert severity="error" data-testid="threshold-history-error">
						The change history could not be loaded. Close this and try again.
					</Alert>
				)}

				{!error && jobs === null && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				)}

				{/* AC5 — a plan that has never had a change applied says so, and says why an empty
				    history is not the same as "the threshold never moved". */}
				{!error && jobs?.length === 0 && (
					<Box sx={{ py: 3 }} data-testid="threshold-history-empty">
						<Typography variant="body1">
							No threshold changes have been applied to current participants for
							this plan.
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Changes made without the &quot;apply to current enrollments&quot;
							option affect future enrollments only.
						</Typography>
					</Box>
				)}

				{/* AC2 — newest first. The backend sorts; nothing is re-ordered here. */}
				{!error &&
					jobs?.map((job) => {
						const counts = historyCounts(job);
						const reachedEveryone = historyReachedEveryone(job);
						const acknowledgment = historyAcknowledgment(job);
						const isExpanded = expandedJobId === job._id;

						return (
							// Each entry is a BOUNDED card. A trailing divider alone left every
							// header reading as the footer of the entry above it — with the date
							// and status chip appearing below the previous change's participant
							// table, which is where it belongs to nothing.
							<Box
								key={job._id}
								sx={{
									p: 2,
									mb: 2,
									border: 1,
									borderColor: "divider",
									borderRadius: 1,
								}}
								data-testid="threshold-history-entry"
							>
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="flex-start"
									spacing={2}
								>
									<Box>
										<Typography variant="subtitle2">
											{formatWhen(job.startedAt)}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{formatActor(job.createdBy)}
										</Typography>
									</Box>

									{/* AC6 — a run that did not reach everyone must NOT read as
									    fully applied. */}
									{reachedEveryone ? (
										<Chip size="small" color="success" label="Applied" />
									) : (
										<Chip
											size="small"
											color="warning"
											label="Reached some participants, but not all"
											data-testid="threshold-history-partial"
										/>
									)}
								</Stack>

								<Typography variant="body2" sx={{ mt: 1 }}>
									Changed {historyWhatChanged(job)}.
								</Typography>

								<Typography variant="body2" color="text.secondary">
									{counts.updated}, {counts.leftAlone}
									{counts.notReached ? `, ${counts.notReached}` : ""}.
								</Typography>

								{acknowledgment && (
									<Typography variant="body2" color="text.secondary">
										{acknowledgment}.
									</Typography>
								)}

								{/* SIMS-9038 — the cohort, per change. Loaded on expand rather than
								    with the list, so the ids never travel N-at-a-time. */}
								<Button
									size="small"
									sx={{ mt: 1 }}
									data-testid="threshold-history-show-participants"
									onClick={() => void toggleParticipants(job._id as string)}
								>
									{isExpanded ? "Hide participants" : "Show participants"}
								</Button>

								{isExpanded && (
									<Box sx={{ mt: 1 }}>
										{loadingCohort && <CircularProgress size={20} />}

										{cohortError && (
											<Alert severity="error">
												The participants could not be loaded.
											</Alert>
										)}

										{/* A job with no records is NOT an empty cohort. Saying
										    "reached nobody" about a run that simply never recorded
										    who it reached would be a confidently wrong answer. */}
										{cohort?.predatesParticipantRecords && (
											<Alert
												severity="info"
												data-testid="threshold-history-predates"
											>
												This change predates per-participant records, so it
												cannot say which participants it covered. The counts
												above still apply.
											</Alert>
										)}

										{cohort &&
											!cohort.predatesParticipantRecords &&
											cohort.participants.length === 0 && (
												<Typography variant="body2" color="text.secondary">
													This change covered no participants.
												</Typography>
											)}

										{cohort &&
											!cohort.predatesParticipantRecords &&
											cohort.participants.length > 0 && (
												<Table
													size="small"
													data-testid="threshold-history-participants"
												>
													<TableHead>
														<TableRow>
															<TableCell>Participant ID</TableCell>
															<TableCell>Enrollment ID</TableCell>
															<TableCell>Outcome</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{cohort.participants.map((p) => {
															const { label, why } = participantOutcomeLabel(
																p,
																cohort.nextValue,
															);
															return (
																<TableRow key={p.enrollmentId}>
																	<TableCell>
																		{/* Absent when the enrollment no longer
																		    resolves — say so rather than showing
																		    a blank cell. */}
																		{p.beneficiaryId ?? "Not available"}
																	</TableCell>
																	<TableCell>{p.enrollmentId}</TableCell>
																	<TableCell>
																		<Typography variant="body2">
																			{label}
																		</Typography>
																		{why && (
																			<Typography
																				variant="caption"
																				color="text.secondary"
																			>
																				{why}
																			</Typography>
																		)}
																	</TableCell>
																</TableRow>
															);
														})}
													</TableBody>
												</Table>
											)}
									</Box>
								)}
							</Box>
						);
					})}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} data-testid="threshold-history-close">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ThresholdHistoryDialog;
